import React, { useState, useRef, useEffect } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  fallback = null,
  lazy = true,
  quality = 80,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  decorative = false,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();

  // Accessibility check: warn if alt is missing and image is not decorative
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !alt && !decorative) {
      console.warn('OptimizedImage: Missing alt text. If this image is decorative, set decorative={true}');
    }
  }, [alt, decorative]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, isInView]);

  // Generate optimized image URL
  const getOptimizedSrc = (originalSrc) => {
    if (!originalSrc) return null;
    
    // If it's already an external URL, return as is
    if (originalSrc.startsWith('http')) {
      return originalSrc;
    }
    
    // For local images, add optimization parameters
    const baseUrl = originalSrc.startsWith('/') ? `/api${originalSrc}` : originalSrc;
    
    // Add quality and format parameters (would work with image optimization service)
    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', 'webp'); // Prefer WebP format
      
      return url.toString();
    } catch (error) {
      // If URL construction fails, return the base URL
      console.warn('Failed to optimize image URL:', error);
      return baseUrl;
    }
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const optimizedSrc = getOptimizedSrc(src);

  // Placeholder component
  const Placeholder = () => (
    <div 
      className={`bg-gray-100 flex items-center justify-center ${className}`}
      role="img"
      aria-label={decorative ? undefined : (alt || 'תמונה לא זמינה')}
    >
      {fallback || <PhotoIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />}
    </div>
  );

  // Error state
  if (hasError) {
    return <Placeholder />;
  }

  // Determine alt text based on decorative flag
  const altText = decorative ? '' : (alt || '');

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {/* Loading placeholder */}
      {!isLoaded && (
        <div 
          className={`absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center ${className}`}
          aria-hidden="true"
        >
          <PhotoIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
        </div>
      )}
      
      {/* Actual image */}
      {isInView && optimizedSrc && (
        <img
          src={optimizedSrc}
          alt={altText}
          className={`${className} ${!isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onLoad={handleLoad}
          onError={handleError}
          loading={lazy ? 'lazy' : 'eager'}
          sizes={sizes}
          role={decorative ? 'presentation' : undefined}
          aria-hidden={decorative ? 'true' : undefined}
          {...props}
        />
      )}
    </div>
  );
};

export default OptimizedImage;

